import type {Mapper} from '@automapper/core';
import {constructUsing, createMap, forMember, mapFrom, mapWith} from '@automapper/core';
import type {IMapping} from '@igp/shared/auto-mapper';
import type {PlayerEcologySummary} from '@igp/shared/contract/pam';
import {
    AgentPlayerReportingV2,
    BonusCostSummary,
    CasinoSummary,
    GlobalSpinsSummary,
    PlayerActivitySummary,
    PokerMttSummary,
    PokerNlheSummary,
    PokerPloSummary,
    PokerSummary,
    ShareSummary,
    SportsSummary,
    TotalGgrSummary,
} from '@igp/shared/contract/pam';
import type {IsNever} from '@igp/shared/utils';

import {
    AgentPlayerReportingV2TypeName,
    PlayerEcologySummaryModelTypeName,
    PlayerEcologySummaryViewModelTypeName,
    PlayerLevelReportV2ColumnSettingKeysTypeName,
    PlayerLevelReportV2DownloadFieldsTypeName,
    PlayerLevelReportV2DownloadModelTypeName,
    PlayerLevelReportV2QueryItemsKeysTypeName,
    PlayerLevelReportV2QuerySummaryKeysTypeName,
    PlayerLevelReportV2SortKeysTypeName,
    PlayerLevelReportV2ViewModelKeysTypeName,
} from './constants';
import type {
    PlayerEcologySummaryViewModel,
    PlayerLevelReportV2ColumnSettingKeys,
    PlayerLevelReportV2DownloadFields,
    PlayerLevelReportV2DownloadModel,
    PlayerLevelReportV2QueryKeys,
    PlayerLevelReportV2SortKeys,
    PlayerLevelReportV2ViewModelKeys,
} from './types';
import {
    BonusCostSummaryViewModel,
    CasinoSummaryViewModel,
    GlobalSpinsSummaryViewModel,
    PlayerActivitySummaryViewModel,
    PlayerLevelReportV2ViewModel,
    PokerMttSummaryViewModel,
    PokerNlheSummaryViewModel,
    PokerPloSummaryViewModel,
    PokerSummaryViewModel,
    ShareSummaryViewModel,
    SportsSummaryViewModel,
    summaryId,
    TotalGgrSummaryViewModel,
} from './types';

export class PlayerLevelReportV2Mapping implements IMapping {
    createMapping(mapper: Mapper): void {
        createMap<BonusCostSummary, BonusCostSummaryViewModel>(
            mapper,
            BonusCostSummary,
            BonusCostSummaryViewModel,
            forMember(
                vm => vm.cash_realized_bonus,
                mapFrom(m => m.cash_realized_bonus)
            ),
            forMember(
                vm => vm.cost_ftd_bonus_pro,
                mapFrom(m => m.cost_ftd_bonus_pro)
            ),
            forMember(
                vm => vm.cost_psp_fee_pro,
                mapFrom(m => m.cost_psp_fee_pro)
            ),
            forMember(
                vm => vm.cost_tournament_overlay,
                mapFrom(m => m.cost_tournament_overlay)
            ),
            forMember(
                vm => vm.cost_tournament_overlay_excl_freeroll,
                mapFrom(m => m.cost_tournament_overlay_excl_freeroll)
            ),
            forMember(
                vm => vm.mtt_ticket_bonus,
                mapFrom(m => m.mtt_ticket_bonus)
            ),
            forMember(
                vm => vm.poker_bonus,
                mapFrom(m => m.poker_bonus)
            ),
            forMember(
                vm => vm.psp_fee,
                mapFrom(m => m.psp_fee)
            )
        );
        createMap<CasinoSummary, CasinoSummaryViewModel>(
            mapper,
            CasinoSummary,
            CasinoSummaryViewModel,
            forMember(
                vm => vm.casino_bonus,
                mapFrom(m => m.casino_bonus)
            ),
            forMember(
                vm => vm.casino_ggr,
                mapFrom(m => m.casino_ggr)
            ),
            forMember(
                vm => vm.casino_ngr,
                mapFrom(m => m.casino_ngr)
            ),
            forMember(
                vm => vm.casino_share,
                mapFrom(m => m.casino_share)
            ),
            forMember(
                vm => vm.external_casino_ggr,
                mapFrom(m => m.external_casino_ggr)
            ),
            forMember(
                vm => vm.internal_casino_ggr,
                mapFrom(m => m.internal_casino_ggr)
            ),
            forMember(
                vm => vm.casino_wager,
                mapFrom(m => m.casino_wager)
            )
        );
        createMap<GlobalSpinsSummary, GlobalSpinsSummaryViewModel>(
            mapper,
            GlobalSpinsSummary,
            GlobalSpinsSummaryViewModel,
            forMember(
                vm => vm.cost_global_spins_tickets,
                mapFrom(m => m.cost_global_spins_tickets)
            ),
            forMember(
                vm => vm.global_spins_expected_ggr,
                mapFrom(m => m.global_spins_expected_ggr)
            )
        );
        createMap<PlayerActivitySummary, PlayerActivitySummaryViewModel>(
            mapper,
            PlayerActivitySummary,
            PlayerActivitySummaryViewModel,
            forMember(
                vm => vm.cash_game_hands_played,
                mapFrom(m => m.cash_game_hands_played)
            ),
            forMember(
                vm => vm.ecology_flag,
                mapFrom(m => m.ecology_flag)
            ),
            forMember(
                vm => vm.mtt_played,
                mapFrom(m => m.mtt_played)
            ),
            forMember(
                vm => vm.player_profit_loss,
                mapFrom(m => m.player_profit_loss)
            ),
            forMember(
                vm => vm.player_win_loss,
                mapFrom(m => m.player_win_loss)
            ),
            forMember(
                vm => vm.net_deposit,
                mapFrom(m => m.net_deposit)
            )
        );
        createMap<PokerMttSummary, PokerMttSummaryViewModel>(
            mapper,
            PokerMttSummary,
            PokerMttSummaryViewModel,
            forMember(
                vm => vm.mtt_ggr,
                mapFrom(m => m.mtt_ggr)
            ),
            forMember(
                vm => vm.mtt_ngr,
                mapFrom(m => m.mtt_ngr)
            ),
            forMember(
                vm => vm.mtt_ticket_bonus,
                mapFrom(m => m.mtt_ticket_bonus)
            ),
            forMember(
                vm => vm.mtt_total_buyin,
                mapFrom(m => m.mtt_total_buyin)
            ),
            forMember(
                vm => vm.player_mtt_win_loss,
                mapFrom(m => m.player_mtt_win_loss)
            ),
            forMember(
                vm => vm.poker_mtt_fees,
                mapFrom(m => m.poker_mtt_fees)
            )
        );
        createMap<PokerNlheSummary, PokerNlheSummaryViewModel>(
            mapper,
            PokerNlheSummary,
            PokerNlheSummaryViewModel,
            forMember(
                vm => vm.is_pro,
                mapFrom(m => m.is_pro)
            ),
            forMember(
                vm => vm.is_pro_shortdeck,
                mapFrom(m => m.is_pro_shortdeck)
            ),
            forMember(
                vm => vm.nlhe_add_on_ggr_usd_pro,
                mapFrom(m => m.nlhe_add_on_ggr_usd_pro)
            ),
            forMember(
                vm => vm.nlhe_ggr_usd_pro,
                mapFrom(m => m.nlhe_ggr_usd_pro)
            ),
            forMember(
                vm => vm.nlhe_insurance_ggr_usd_pro,
                mapFrom(m => m.nlhe_insurance_ggr_usd_pro)
            ),
            forMember(
                vm => vm.nlhe_jackpot_ggr_usd_pro,
                mapFrom(m => m.nlhe_jackpot_ggr_usd_pro)
            ),
            forMember(
                vm => vm.nlhe_rakes_ggr_usd_pro,
                mapFrom(m => m.nlhe_rakes_ggr_usd_pro)
            ),
            forMember(
                vm => vm.nlhe_micro_ggr_usd_pro,
                mapFrom(m => m.nlhe_micro_ggr_usd_pro)
            ),
            forMember(
                vm => vm.nlhe_low_ggr_usd_pro,
                mapFrom(m => m.nlhe_low_ggr_usd_pro)
            ),
            forMember(
                vm => vm.nlhe_medium_ggr_usd_pro,
                mapFrom(m => m.nlhe_medium_ggr_usd_pro)
            ),
            forMember(
                vm => vm.nlhe_high_ggr_usd_pro,
                mapFrom(m => m.nlhe_high_ggr_usd_pro)
            ),
            forMember(
                vm => vm.player_nlhe_shortdeck_win_loss_lifetime,
                mapFrom(m => m.player_nlhe_shortdeck_win_loss_lifetime)
            ),
            forMember(
                vm => vm.ggr_poker_prev_180_days,
                mapFrom(m => m.ggr_poker_prev_180_days)
            ),
            forMember(
                vm => vm.player_nlhe_shortdeck_win_loss_prev_180_days,
                mapFrom(m => m.player_nlhe_shortdeck_win_loss_prev_180_days)
            ),
            forMember(
                vm => vm.shortdeck_add_on_ggr_usd_pro,
                mapFrom(m => m.shortdeck_add_on_ggr_usd_pro)
            ),
            forMember(
                vm => vm.shortdeck_ggr_usd_pro,
                mapFrom(m => m.shortdeck_ggr_usd_pro)
            ),
            forMember(
                vm => vm.shortdeck_insurance_ggr_usd_pro,
                mapFrom(m => m.shortdeck_insurance_ggr_usd_pro)
            ),
            forMember(
                vm => vm.shortdeck_rakes_ggr_usd_pro,
                mapFrom(m => m.shortdeck_rakes_ggr_usd_pro)
            )
        );
        createMap<PokerPloSummary, PokerPloSummaryViewModel>(
            mapper,
            PokerPloSummary,
            PokerPloSummaryViewModel,
            forMember(
                vm => vm.plo_ggr,
                mapFrom(m => m.plo_ggr)
            )
        );
        createMap<PokerSummary, PokerSummaryViewModel>(
            mapper,
            PokerSummary,
            PokerSummaryViewModel,
            forMember(
                vm => vm.poker_addons,
                mapFrom(m => m.poker_addons)
            ),
            forMember(
                vm => vm.poker_addons_add_on,
                mapFrom(m => m.poker_addons_add_on)
            ),
            forMember(
                vm => vm.poker_addons_guess,
                mapFrom(m => m.poker_addons_guess)
            ),
            forMember(
                vm => vm.poker_addons_insurance,
                mapFrom(m => m.poker_addons_insurance)
            ),
            forMember(
                vm => vm.poker_ggr,
                mapFrom(m => m.poker_ggr)
            ),
            forMember(
                vm => vm.poker_ggr_usd_pro_ecology_modified,
                mapFrom(m => m.poker_ggr_usd_pro_ecology_modified)
            ),
            forMember(
                vm => vm.poker_ngr,
                mapFrom(m => m.poker_ngr)
            ),
            forMember(
                vm => vm.poker_rake,
                mapFrom(m => m.poker_rake)
            ),
            forMember(
                vm => vm.poker_share,
                mapFrom(m => m.poker_share)
            )
        );
        createMap<ShareSummary, ShareSummaryViewModel>(
            mapper,
            ShareSummary,
            ShareSummaryViewModel,
            forMember(
                vm => vm.casino_share,
                mapFrom(m => m.casino_share)
            ),
            forMember(
                vm => vm.global_spins_expected_ggr,
                mapFrom(m => m.global_spins_expected_ggr)
            ),
            forMember(
                vm => vm.mtt_ggr,
                mapFrom(m => m.mtt_ggr)
            ),
            forMember(
                vm => vm.plo_ggr,
                mapFrom(m => m.plo_ggr)
            ),
            forMember(
                vm => vm.poker_share,
                mapFrom(m => m.poker_share)
            ),
            forMember(
                vm => vm.sports_settled_bet,
                mapFrom(m => m.sports_settled_bet)
            )
        );
        createMap<SportsSummary, SportsSummaryViewModel>(
            mapper,
            SportsSummary,
            SportsSummaryViewModel,
            forMember(
                vm => vm.sports_ggr,
                mapFrom(m => m.sports_ggr)
            ),
            forMember(
                vm => vm.sports_settled_bet,
                mapFrom(m => m.sports_settled_bet)
            )
        );
        createMap<PlayerEcologySummary, PlayerEcologySummaryViewModel>(
            mapper,
            PlayerEcologySummaryModelTypeName,
            PlayerEcologySummaryViewModelTypeName,
            forMember(
                vm => vm.ecology_flag,
                mapFrom(m => m.ecology_flag)
            ),
            forMember(
                vm => vm.is_pro_shortdeck,
                mapFrom(m => m.is_pro_shortdeck)
            ),
            forMember(
                vm => vm.is_pro_zoom,
                mapFrom(m => m.is_pro_zoom)
            ),
            forMember(
                vm => vm.stake_2_is_pro_nlhe,
                mapFrom(m => m.stake_2_is_pro_nlhe)
            ),
            forMember(
                vm => vm.stake_4_is_pro_nlhe,
                mapFrom(m => m.stake_4_is_pro_nlhe)
            ),
            forMember(
                vm => vm.stake_10_is_pro_nlhe,
                mapFrom(m => m.stake_10_is_pro_nlhe)
            ),
            forMember(
                vm => vm.stake_20_is_pro_nlhe,
                mapFrom(m => m.stake_20_is_pro_nlhe)
            ),
            forMember(
                vm => vm.stake_50_is_pro_nlhe,
                mapFrom(m => m.stake_50_is_pro_nlhe)
            ),
            forMember(
                vm => vm.stake_100_is_pro_nlhe,
                mapFrom(m => m.stake_100_is_pro_nlhe)
            ),
            forMember(
                vm => vm.stake_200_is_pro_nlhe,
                mapFrom(m => m.stake_200_is_pro_nlhe)
            ),
            forMember(
                vm => vm.stake_400_is_pro_nlhe,
                mapFrom(m => m.stake_400_is_pro_nlhe)
            )
        );
        createMap<TotalGgrSummary, TotalGgrSummaryViewModel>(
            mapper,
            TotalGgrSummary,
            TotalGgrSummaryViewModel,
            forMember(
                vm => vm.ggr,
                mapFrom(m => m.ggr)
            )
        );
        createMap<AgentPlayerReportingV2, PlayerLevelReportV2ViewModel>(
            mapper,
            AgentPlayerReportingV2,
            PlayerLevelReportV2ViewModel,
            forMember(
                vm => vm.total_ggr_summary,
                mapWith(TotalGgrSummaryViewModel, TotalGgrSummary, m => m.total_ggr_summary)
            ),
            forMember(
                vm => vm.isSummary,
                mapFrom(m => m.uid === summaryId)
            ),
            forMember(
                vm => vm.bonus_cost_summary,
                mapWith(BonusCostSummaryViewModel, BonusCostSummary, m => m.bonus_cost_summary)
            ),
            forMember(
                vm => vm.casino_summary,
                mapWith(CasinoSummaryViewModel, CasinoSummary, m => m.casino_summary)
            ),
            forMember(
                vm => vm.global_spins_summary,
                mapWith(GlobalSpinsSummaryViewModel, GlobalSpinsSummary, m => m.global_spins_summary)
            ),
            forMember(
                vm => vm.iso_alpha2_country_code,
                mapFrom(m => m.iso_alpha2_country_code)
            ),
            forMember(
                vm => vm.path,
                mapFrom(m => m.path)
            ),
            forMember(
                vm => vm.player_activity_summary,
                mapWith(PlayerActivitySummaryViewModel, PlayerActivitySummary, m => m.player_activity_summary)
            ),
            forMember(
                vm => vm.player_ecology,
                mapWith(PlayerEcologySummaryViewModelTypeName, PlayerEcologySummaryModelTypeName, m => m.player_ecology)
            ),
            forMember(
                vm => vm.poker_mtt_summary,
                mapWith(PokerMttSummaryViewModel, PokerMttSummary, m => m.poker_mtt_summary)
            ),
            forMember(
                vm => vm.poker_nlhe_summary,
                mapWith(PokerNlheSummaryViewModel, PokerNlheSummary, m => m.poker_nlhe_summary)
            ),
            forMember(
                vm => vm.poker_plo_summary,
                mapWith(PokerPloSummaryViewModel, PokerPloSummary, m => m.poker_plo_summary)
            ),
            forMember(
                vm => vm.poker_summary,
                mapWith(PokerSummaryViewModel, PokerSummary, m => m.poker_summary)
            ),
            forMember(
                vm => vm.referrer_agent_id,
                mapFrom(m => m.referrer_agent_id)
            ),
            forMember(
                vm => vm.referrer_agent_username,
                mapFrom(m => m.referrer_agent_username)
            ),
            forMember(
                vm => vm.share_summary,
                mapWith(ShareSummaryViewModel, ShareSummary, m => m.share_summary)
            ),
            forMember(
                vm => vm.sports_summary,
                mapWith(SportsSummaryViewModel, SportsSummary, m => m.sports_summary)
            ),
            forMember(
                vm => vm.uid,
                mapFrom(m => m.uid)
            ),
            forMember(
                vm => vm.username,
                mapFrom(m => m.username)
            )
        );
        createMap<AgentPlayerReportingV2, PlayerLevelReportV2DownloadModel>(
            mapper,
            AgentPlayerReportingV2TypeName,
            PlayerLevelReportV2DownloadModelTypeName,
            forMember(
                vm => vm.iso_alpha2_country_code,
                mapFrom(m => m.iso_alpha2_country_code)
            ),
            forMember(
                vm => vm.path,
                mapFrom(m => m.path)
            ),
            forMember(
                vm => vm.referrer_agent_id,
                mapFrom(m => m.referrer_agent_id)
            ),
            forMember(
                vm => vm.referrer_agent_username,
                mapFrom(m => m.referrer_agent_username)
            ),
            forMember(
                vm => vm.uid,
                mapFrom(m => m.uid)
            ),
            forMember(
                vm => vm['total_ggr_summary.ggr'],
                mapFrom(m => m.total_ggr_summary?.ggr?.toString())
            ),
            forMember(
                vm => vm.username,
                mapFrom(m => m.username)
            ),
            forMember(
                vm => vm['bonus_cost_summary.cash_realized_bonus'],
                mapFrom(m => m.bonus_cost_summary?.cash_realized_bonus?.toString())
            ),
            forMember(
                vm => vm['bonus_cost_summary.cost_ftd_bonus_pro'],
                mapFrom(m => m.bonus_cost_summary?.cost_ftd_bonus_pro?.toString())
            ),
            forMember(
                vm => vm['bonus_cost_summary.cost_psp_fee_pro'],
                mapFrom(m => m.bonus_cost_summary?.cost_psp_fee_pro?.toString())
            ),
            forMember(
                vm => vm['bonus_cost_summary.cost_tournament_overlay'],
                mapFrom(m => m.bonus_cost_summary?.cost_tournament_overlay?.toString())
            ),
            forMember(
                vm => vm['bonus_cost_summary.cost_tournament_overlay_excl_freeroll'],
                mapFrom(m => m.bonus_cost_summary?.cost_tournament_overlay_excl_freeroll?.toString())
            ),
            forMember(
                vm => vm['bonus_cost_summary.mtt_ticket_bonus'],
                mapFrom(m => m.bonus_cost_summary?.mtt_ticket_bonus?.toString())
            ),
            forMember(
                vm => vm['bonus_cost_summary.poker_bonus'],
                mapFrom(m => m.bonus_cost_summary?.poker_bonus?.toString())
            ),
            forMember(
                vm => vm['bonus_cost_summary.psp_fee'],
                mapFrom(m => m.bonus_cost_summary?.psp_fee?.toString())
            ),
            forMember(
                vm => vm['casino_summary.casino_bonus'],
                mapFrom(m => m.casino_summary?.casino_bonus?.toString())
            ),
            forMember(
                vm => vm['casino_summary.casino_ggr'],
                mapFrom(m => m.casino_summary?.casino_ggr?.toString())
            ),
            forMember(
                vm => vm['casino_summary.casino_ngr'],
                mapFrom(m => m.casino_summary?.casino_ngr?.toString())
            ),
            forMember(
                vm => vm['casino_summary.casino_share'],
                mapFrom(m => m.casino_summary?.casino_share?.toString())
            ),
            forMember(
                vm => vm['casino_summary.external_casino_ggr'],
                mapFrom(m => m.casino_summary?.external_casino_ggr?.toString())
            ),
            forMember(
                vm => vm['casino_summary.internal_casino_ggr'],
                mapFrom(m => m.casino_summary?.internal_casino_ggr?.toString())
            ),
            forMember(
                vm => vm['casino_summary.casino_wager'],
                mapFrom(m => m.casino_summary?.casino_wager?.toString())
            ),
            forMember(
                vm => vm['global_spins_summary.cost_global_spins_tickets'],
                mapFrom(m => m.global_spins_summary?.cost_global_spins_tickets?.toString())
            ),
            forMember(
                vm => vm['global_spins_summary.global_spins_expected_ggr'],
                mapFrom(m => m.global_spins_summary?.global_spins_expected_ggr?.toString())
            ),
            forMember(
                vm => vm['global_spins_summary.global_spins_ggr'],
                mapFrom(m => m.global_spins_summary?.global_spins_expected_ggr?.toString())
            ),
            forMember(
                vm => vm['player_activity_summary.cash_game_hands_played'],
                mapFrom(m => m.player_activity_summary?.cash_game_hands_played?.toString())
            ),
            forMember(
                vm => vm['player_activity_summary.ecology_flag'],
                mapFrom(m => m.player_activity_summary?.ecology_flag)
            ),
            forMember(
                vm => vm['player_activity_summary.mtt_played'],
                mapFrom(m => m.player_activity_summary?.mtt_played?.toString())
            ),
            forMember(
                vm => vm['player_activity_summary.player_profit_loss'],
                mapFrom(m => m.player_activity_summary?.player_profit_loss?.toString())
            ),
            forMember(
                vm => vm['player_activity_summary.player_win_loss'],
                mapFrom(m => m.player_activity_summary?.player_win_loss?.toString())
            ),
            forMember(
                vm => vm['player_activity_summary.net_deposit'],
                mapFrom(m => m.player_activity_summary?.net_deposit?.toString())
            ),
            forMember(
                vm => vm['player_ecology.ecology_flag'],
                mapFrom(m => m.player_ecology?.ecology_flag)
            ),
            forMember(
                vm => vm['player_ecology.is_pro_shortdeck'],
                mapFrom(m => m.player_ecology?.is_pro_shortdeck?.toString())
            ),
            forMember(
                vm => vm['player_ecology.is_pro_zoom'],
                mapFrom(m => m.player_ecology?.is_pro_zoom?.toString())
            ),
            forMember(
                vm => vm['player_ecology.stake_2_is_pro_nlhe'],
                mapFrom(m => m.player_ecology?.stake_2_is_pro_nlhe?.toString())
            ),
            forMember(
                vm => vm['player_ecology.stake_4_is_pro_nlhe'],
                mapFrom(m => m.player_ecology?.stake_4_is_pro_nlhe?.toString())
            ),
            forMember(
                vm => vm['player_ecology.stake_10_is_pro_nlhe'],
                mapFrom(m => m.player_ecology?.stake_10_is_pro_nlhe?.toString())
            ),
            forMember(
                vm => vm['player_ecology.stake_20_is_pro_nlhe'],
                mapFrom(m => m.player_ecology?.stake_20_is_pro_nlhe?.toString())
            ),
            forMember(
                vm => vm['player_ecology.stake_50_is_pro_nlhe'],
                mapFrom(m => m.player_ecology?.stake_50_is_pro_nlhe?.toString())
            ),
            forMember(
                vm => vm['player_ecology.stake_100_is_pro_nlhe'],
                mapFrom(m => m.player_ecology?.stake_100_is_pro_nlhe?.toString())
            ),
            forMember(
                vm => vm['player_ecology.stake_200_is_pro_nlhe'],
                mapFrom(m => m.player_ecology?.stake_200_is_pro_nlhe?.toString())
            ),
            forMember(
                vm => vm['player_ecology.stake_400_is_pro_nlhe'],
                mapFrom(m => m.player_ecology?.stake_400_is_pro_nlhe?.toString())
            ),
            forMember(
                vm => vm['poker_mtt_summary.mtt_ticket_bonus'],
                mapFrom(m => m.poker_mtt_summary?.mtt_ticket_bonus?.toString())
            ),
            forMember(
                vm => vm['poker_mtt_summary.mtt_ggr'],
                mapFrom(m => m.poker_mtt_summary?.mtt_ggr?.toString())
            ),
            forMember(
                vm => vm['poker_mtt_summary.mtt_ngr'],
                mapFrom(m => m.poker_mtt_summary?.mtt_ngr?.toString())
            ),
            forMember(
                vm => vm['poker_mtt_summary.mtt_total_buyin'],
                mapFrom(m => m.poker_mtt_summary?.mtt_total_buyin?.toString())
            ),
            forMember(
                vm => vm['poker_mtt_summary.player_mtt_win_loss'],
                mapFrom(m => m.poker_mtt_summary?.player_mtt_win_loss?.toString())
            ),
            forMember(
                vm => vm['poker_mtt_summary.poker_mtt_fees'],
                mapFrom(m => m.poker_mtt_summary?.poker_mtt_fees?.toString())
            ),
            forMember(
                vm => vm['poker_nlhe_summary.is_pro'],
                mapFrom(m => m.poker_nlhe_summary?.is_pro?.toString())
            ),
            forMember(
                vm => vm['poker_nlhe_summary.is_pro_shortdeck'],
                mapFrom(m => m.poker_nlhe_summary?.is_pro_shortdeck?.toString())
            ),
            forMember(
                vm => vm['poker_nlhe_summary.nlhe_add_on_ggr_usd_pro'],
                mapFrom(m => m.poker_nlhe_summary?.nlhe_add_on_ggr_usd_pro?.toString())
            ),
            forMember(
                vm => vm['poker_nlhe_summary.nlhe_ggr_usd_pro'],
                mapFrom(m => m.poker_nlhe_summary?.nlhe_ggr_usd_pro?.toString())
            ),
            forMember(
                vm => vm['poker_nlhe_summary.nlhe_insurance_ggr_usd_pro'],
                mapFrom(m => m.poker_nlhe_summary?.nlhe_insurance_ggr_usd_pro?.toString())
            ),
            forMember(
                vm => vm['poker_nlhe_summary.nlhe_jackpot_ggr_usd_pro'],
                mapFrom(m => m.poker_nlhe_summary?.nlhe_jackpot_ggr_usd_pro?.toString())
            ),
            forMember(
                vm => vm['poker_nlhe_summary.nlhe_rakes_ggr_usd_pro'],
                mapFrom(m => m.poker_nlhe_summary?.nlhe_rakes_ggr_usd_pro?.toString())
            ),
            forMember(
                vm => vm['poker_nlhe_summary.nlhe_micro_ggr_usd_pro'],
                mapFrom(m => m.poker_nlhe_summary?.nlhe_micro_ggr_usd_pro?.toString())
            ),
            forMember(
                vm => vm['poker_nlhe_summary.nlhe_low_ggr_usd_pro'],
                mapFrom(m => m.poker_nlhe_summary?.nlhe_low_ggr_usd_pro?.toString())
            ),
            forMember(
                vm => vm['poker_nlhe_summary.nlhe_medium_ggr_usd_pro'],
                mapFrom(m => m.poker_nlhe_summary?.nlhe_medium_ggr_usd_pro?.toString())
            ),
            forMember(
                vm => vm['poker_nlhe_summary.nlhe_high_ggr_usd_pro'],
                mapFrom(m => m.poker_nlhe_summary?.nlhe_high_ggr_usd_pro?.toString())
            ),
            forMember(
                vm => vm['poker_nlhe_summary.player_nlhe_shortdeck_win_loss_lifetime'],
                mapFrom(m => m.poker_nlhe_summary?.player_nlhe_shortdeck_win_loss_lifetime?.toString())
            ),
            forMember(
                vm => vm['poker_nlhe_summary.ggr_poker_prev_180_days'],
                mapFrom(m => m.poker_nlhe_summary?.ggr_poker_prev_180_days?.toString())
            ),
            forMember(
                vm => vm['poker_nlhe_summary.player_nlhe_shortdeck_win_loss_prev_180_days'],
                mapFrom(m => m.poker_nlhe_summary?.player_nlhe_shortdeck_win_loss_prev_180_days?.toString())
            ),
            forMember(
                vm => vm['poker_nlhe_summary.shortdeck_add_on_ggr_usd_pro'],
                mapFrom(m => m.poker_nlhe_summary?.shortdeck_add_on_ggr_usd_pro?.toString())
            ),
            forMember(
                vm => vm['poker_nlhe_summary.shortdeck_ggr_usd_pro'],
                mapFrom(m => m.poker_nlhe_summary?.shortdeck_ggr_usd_pro?.toString())
            ),
            forMember(
                vm => vm['poker_nlhe_summary.shortdeck_insurance_ggr_usd_pro'],
                mapFrom(m => m.poker_nlhe_summary?.shortdeck_insurance_ggr_usd_pro?.toString())
            ),
            forMember(
                vm => vm['poker_nlhe_summary.shortdeck_rakes_ggr_usd_pro'],
                mapFrom(m => m.poker_nlhe_summary?.shortdeck_rakes_ggr_usd_pro?.toString())
            ),
            forMember(
                vm => vm['poker_plo_summary.plo_ggr'],
                mapFrom(m => m.poker_plo_summary?.plo_ggr?.toString())
            ),
            forMember(
                vm => vm['poker_summary.poker_addons'],
                mapFrom(m => m.poker_summary?.poker_addons?.toString())
            ),
            forMember(
                vm => vm['poker_summary.poker_addons_add_on'],
                mapFrom(m => m.poker_summary?.poker_addons_add_on?.toString())
            ),
            forMember(
                vm => vm['poker_summary.poker_addons_guess'],
                mapFrom(m => m.poker_summary?.poker_addons_guess?.toString())
            ),
            forMember(
                vm => vm['poker_summary.poker_addons_insurance'],
                mapFrom(m => m.poker_summary?.poker_addons_insurance?.toString())
            ),
            forMember(
                vm => vm['poker_summary.poker_ggr'],
                mapFrom(m => m.poker_summary?.poker_ggr?.toString())
            ),
            forMember(
                vm => vm['poker_summary.poker_ggr_usd_pro_ecology_modified'],
                mapFrom(m => m.poker_summary?.poker_ggr_usd_pro_ecology_modified?.toString())
            ),
            forMember(
                vm => vm['poker_summary.poker_ngr'],
                mapFrom(m => m.poker_summary?.poker_ngr?.toString())
            ),
            forMember(
                vm => vm['poker_summary.poker_rake'],
                mapFrom(m => m.poker_summary?.poker_rake?.toString())
            ),
            forMember(
                vm => vm['poker_summary.poker_share'],
                mapFrom(m => m.poker_summary?.poker_share?.toString())
            ),
            forMember(
                vm => vm['share_summary.casino_share'],
                mapFrom(m => m.share_summary?.casino_share?.toString())
            ),
            forMember(
                vm => vm['share_summary.global_spins_expected_ggr'],
                mapFrom(m => m.share_summary?.global_spins_expected_ggr?.toString())
            ),
            forMember(
                vm => vm['share_summary.mtt_ggr'],
                mapFrom(m => m.share_summary?.mtt_ggr?.toString())
            ),
            forMember(
                vm => vm['share_summary.plo_ggr'],
                mapFrom(m => m.share_summary?.plo_ggr?.toString())
            ),
            forMember(
                vm => vm['share_summary.poker_share'],
                mapFrom(m => m.share_summary?.poker_share?.toString())
            ),
            forMember(
                vm => vm['share_summary.sports_settled_bet'],
                mapFrom(m => m.share_summary?.sports_settled_bet?.toString())
            ),
            forMember(
                vm => vm['sports_summary.sports_settled_bet'],
                mapFrom(m => m.sports_summary?.sports_settled_bet?.toString())
            ),
            forMember(
                vm => vm['sports_summary.sports_ggr'],
                mapFrom(m => m.sports_summary?.sports_ggr?.toString())
            )
        );
        createMap<PlayerLevelReportV2ViewModelKeys, PlayerLevelReportV2QueryKeys>(
            mapper,
            PlayerLevelReportV2ViewModelKeysTypeName,
            PlayerLevelReportV2QueryItemsKeysTypeName,
            constructUsing(m => {
                const itemsMapper: Record<PlayerLevelReportV2ViewModelKeys, PlayerLevelReportV2QueryKeys> = {
                    iso_alpha2_country_code: 'items.iso_alpha2_country_code',
                    path: 'items.path',
                    referrer_agent_id: 'items.referrer_agent_id',
                    referrer_agent_username: 'items.referrer_agent_username',
                    uid: 'items.uid',
                    username: 'items.username',
                    'total_ggr_summary.ggr': 'items.total_ggr_summary.ggr',
                    'bonus_cost_summary.cash_realized_bonus': 'items.bonus_cost_summary.cash_realized_bonus',
                    'bonus_cost_summary.cost_ftd_bonus_pro': 'items.bonus_cost_summary.cost_ftd_bonus_pro',
                    'bonus_cost_summary.cost_psp_fee_pro': 'items.bonus_cost_summary.cost_psp_fee_pro',
                    'bonus_cost_summary.cost_tournament_overlay': 'items.bonus_cost_summary.cost_tournament_overlay',
                    'bonus_cost_summary.cost_tournament_overlay_excl_freeroll':
                        'items.bonus_cost_summary.cost_tournament_overlay_excl_freeroll',
                    'bonus_cost_summary.mtt_ticket_bonus': 'items.bonus_cost_summary.mtt_ticket_bonus',
                    'bonus_cost_summary.poker_bonus': 'items.bonus_cost_summary.poker_bonus',
                    'bonus_cost_summary.psp_fee': 'items.bonus_cost_summary.psp_fee',
                    'casino_summary.casino_bonus': 'items.casino_summary.casino_bonus',
                    'casino_summary.casino_ggr': 'items.casino_summary.casino_ggr',
                    'casino_summary.casino_ngr': 'items.casino_summary.casino_ngr',
                    'casino_summary.casino_share': 'items.casino_summary.casino_share',
                    'casino_summary.external_casino_ggr': 'items.casino_summary.external_casino_ggr',
                    'casino_summary.internal_casino_ggr': 'items.casino_summary.internal_casino_ggr',
                    'casino_summary.casino_wager': 'items.casino_summary.casino_wager',
                    'global_spins_summary.cost_global_spins_tickets': 'items.global_spins_summary.cost_global_spins_tickets',
                    'global_spins_summary.global_spins_expected_ggr': 'items.global_spins_summary.global_spins_expected_ggr',
                    'global_spins_summary.global_spins_ggr': 'items.global_spins_summary.global_spins_expected_ggr',
                    'player_activity_summary.cash_game_hands_played': 'items.player_activity_summary.cash_game_hands_played',
                    'player_activity_summary.ecology_flag': 'items.player_activity_summary.ecology_flag',
                    'player_activity_summary.mtt_played': 'items.player_activity_summary.mtt_played',
                    'player_activity_summary.player_profit_loss': 'items.player_activity_summary.player_profit_loss',
                    'player_activity_summary.player_win_loss': 'items.player_activity_summary.player_win_loss',
                    'player_activity_summary.net_deposit': 'items.player_activity_summary.net_deposit',
                    'player_ecology.ecology_flag': 'items.player_ecology.ecology_flag',
                    'player_ecology.is_pro_shortdeck': 'items.player_ecology.is_pro_shortdeck',
                    'player_ecology.is_pro_zoom': 'items.player_ecology.is_pro_zoom',
                    'player_ecology.stake_2_is_pro_nlhe': 'items.player_ecology.stake_2_is_pro_nlhe',
                    'player_ecology.stake_4_is_pro_nlhe': 'items.player_ecology.stake_4_is_pro_nlhe',
                    'player_ecology.stake_10_is_pro_nlhe': 'items.player_ecology.stake_10_is_pro_nlhe',
                    'player_ecology.stake_20_is_pro_nlhe': 'items.player_ecology.stake_20_is_pro_nlhe',
                    'player_ecology.stake_50_is_pro_nlhe': 'items.player_ecology.stake_50_is_pro_nlhe',
                    'player_ecology.stake_100_is_pro_nlhe': 'items.player_ecology.stake_100_is_pro_nlhe',
                    'player_ecology.stake_200_is_pro_nlhe': 'items.player_ecology.stake_200_is_pro_nlhe',
                    'player_ecology.stake_400_is_pro_nlhe': 'items.player_ecology.stake_400_is_pro_nlhe',
                    'poker_mtt_summary.mtt_ticket_bonus': 'items.poker_mtt_summary.mtt_ticket_bonus',
                    'poker_mtt_summary.mtt_ggr': 'items.poker_mtt_summary.mtt_ggr',
                    'poker_mtt_summary.mtt_ngr': 'items.poker_mtt_summary.mtt_ngr',
                    'poker_mtt_summary.mtt_total_buyin': 'items.poker_mtt_summary.mtt_total_buyin',
                    'poker_mtt_summary.player_mtt_win_loss': 'items.poker_mtt_summary.player_mtt_win_loss',
                    'poker_mtt_summary.poker_mtt_fees': 'items.poker_mtt_summary.poker_mtt_fees',
                    'poker_nlhe_summary.is_pro': 'items.poker_nlhe_summary.is_pro',
                    'poker_nlhe_summary.is_pro_shortdeck': 'items.poker_nlhe_summary.is_pro_shortdeck',
                    'poker_nlhe_summary.nlhe_add_on_ggr_usd_pro': 'items.poker_nlhe_summary.nlhe_add_on_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_ggr_usd_pro': 'items.poker_nlhe_summary.nlhe_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_insurance_ggr_usd_pro': 'items.poker_nlhe_summary.nlhe_insurance_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_jackpot_ggr_usd_pro': 'items.poker_nlhe_summary.nlhe_jackpot_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_rakes_ggr_usd_pro': 'items.poker_nlhe_summary.nlhe_rakes_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_micro_ggr_usd_pro': 'items.poker_nlhe_summary.nlhe_micro_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_low_ggr_usd_pro': 'items.poker_nlhe_summary.nlhe_low_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_medium_ggr_usd_pro': 'items.poker_nlhe_summary.nlhe_medium_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_high_ggr_usd_pro': 'items.poker_nlhe_summary.nlhe_high_ggr_usd_pro',
                    'poker_nlhe_summary.player_nlhe_shortdeck_win_loss_lifetime':
                        'items.poker_nlhe_summary.player_nlhe_shortdeck_win_loss_lifetime',
                    'poker_nlhe_summary.ggr_poker_prev_180_days': 'items.poker_nlhe_summary.ggr_poker_prev_180_days',
                    'poker_nlhe_summary.player_nlhe_shortdeck_win_loss_prev_180_days':
                        'items.poker_nlhe_summary.player_nlhe_shortdeck_win_loss_prev_180_days',
                    'poker_nlhe_summary.shortdeck_add_on_ggr_usd_pro': 'items.poker_nlhe_summary.shortdeck_add_on_ggr_usd_pro',
                    'poker_nlhe_summary.shortdeck_ggr_usd_pro': 'items.poker_nlhe_summary.shortdeck_ggr_usd_pro',
                    'poker_nlhe_summary.shortdeck_insurance_ggr_usd_pro': 'items.poker_nlhe_summary.shortdeck_insurance_ggr_usd_pro',
                    'poker_nlhe_summary.shortdeck_rakes_ggr_usd_pro': 'items.poker_nlhe_summary.shortdeck_rakes_ggr_usd_pro',
                    'poker_plo_summary.plo_ggr': 'items.poker_plo_summary.plo_ggr',
                    'poker_summary.poker_addons': 'items.poker_summary.poker_addons',
                    'poker_summary.poker_addons_add_on': 'items.poker_summary.poker_addons_add_on',
                    'poker_summary.poker_addons_guess': 'items.poker_summary.poker_addons_guess',
                    'poker_summary.poker_addons_insurance': 'items.poker_summary.poker_addons_insurance',
                    'poker_summary.poker_ggr': 'items.poker_summary.poker_ggr',
                    'poker_summary.poker_ggr_usd_pro_ecology_modified': 'items.poker_summary.poker_ggr_usd_pro_ecology_modified',
                    'poker_summary.poker_ngr': 'items.poker_summary.poker_ngr',
                    'poker_summary.poker_rake': 'items.poker_summary.poker_rake',
                    'poker_summary.poker_share': 'items.poker_summary.poker_share',
                    'share_summary.casino_share': 'items.share_summary.casino_share',
                    'share_summary.global_spins_expected_ggr': 'items.share_summary.global_spins_expected_ggr',
                    'share_summary.mtt_ggr': 'items.share_summary.mtt_ggr',
                    'share_summary.plo_ggr': 'items.share_summary.plo_ggr',
                    'share_summary.poker_share': 'items.share_summary.poker_share',
                    'share_summary.sports_settled_bet': 'items.share_summary.sports_settled_bet',
                    'sports_summary.sports_settled_bet': 'items.sports_summary.sports_settled_bet',
                    'sports_summary.sports_ggr': 'items.sports_summary.sports_ggr',
                };

                return itemsMapper[m];
            })
        );
        createMap<PlayerLevelReportV2ViewModelKeys, PlayerLevelReportV2QueryKeys>(
            mapper,
            PlayerLevelReportV2ViewModelKeysTypeName,
            PlayerLevelReportV2QuerySummaryKeysTypeName,
            constructUsing(m => {
                const summaryMapper: Record<PlayerLevelReportV2ViewModelKeys, PlayerLevelReportV2QueryKeys> = {
                    iso_alpha2_country_code: null,
                    path: null,
                    referrer_agent_id: null,
                    referrer_agent_username: null,
                    uid: null,
                    username: null,
                    'total_ggr_summary.ggr': 'summary.total_ggr_summary.ggr',
                    'bonus_cost_summary.cash_realized_bonus': 'summary.bonus_cost_summary.cash_realized_bonus',
                    'bonus_cost_summary.cost_ftd_bonus_pro': 'summary.bonus_cost_summary.cost_ftd_bonus_pro',
                    'bonus_cost_summary.cost_psp_fee_pro': 'summary.bonus_cost_summary.cost_psp_fee_pro',
                    'bonus_cost_summary.cost_tournament_overlay': 'summary.bonus_cost_summary.cost_tournament_overlay',
                    'bonus_cost_summary.cost_tournament_overlay_excl_freeroll':
                        'summary.bonus_cost_summary.cost_tournament_overlay_excl_freeroll',
                    'bonus_cost_summary.mtt_ticket_bonus': 'summary.bonus_cost_summary.mtt_ticket_bonus',
                    'bonus_cost_summary.poker_bonus': 'summary.bonus_cost_summary.poker_bonus',
                    'bonus_cost_summary.psp_fee': 'summary.bonus_cost_summary.psp_fee',
                    'casino_summary.casino_bonus': 'summary.casino_summary.casino_bonus',
                    'casino_summary.casino_ggr': 'summary.casino_summary.casino_ggr',
                    'casino_summary.casino_ngr': 'summary.casino_summary.casino_ngr',
                    'casino_summary.casino_share': 'summary.casino_summary.casino_share',
                    'casino_summary.external_casino_ggr': 'summary.casino_summary.external_casino_ggr',
                    'casino_summary.internal_casino_ggr': 'summary.casino_summary.internal_casino_ggr',
                    'casino_summary.casino_wager': 'summary.casino_summary.casino_wager',
                    'global_spins_summary.cost_global_spins_tickets': 'summary.global_spins_summary.cost_global_spins_tickets',
                    'global_spins_summary.global_spins_expected_ggr': 'summary.global_spins_summary.global_spins_expected_ggr',
                    'global_spins_summary.global_spins_ggr': 'summary.global_spins_summary.global_spins_expected_ggr',
                    'player_activity_summary.cash_game_hands_played': 'summary.player_activity_summary.cash_game_hands_played',
                    'player_activity_summary.ecology_flag': null,
                    'player_activity_summary.mtt_played': 'summary.player_activity_summary.mtt_played',
                    'player_activity_summary.player_profit_loss': 'summary.player_activity_summary.player_profit_loss',
                    'player_activity_summary.player_win_loss': 'summary.player_activity_summary.player_win_loss',
                    'player_activity_summary.net_deposit': 'summary.player_activity_summary.net_deposit',
                    'player_ecology.ecology_flag': null,
                    'player_ecology.is_pro_shortdeck': null,
                    'player_ecology.is_pro_zoom': null,
                    'player_ecology.stake_2_is_pro_nlhe': null,
                    'player_ecology.stake_4_is_pro_nlhe': null,
                    'player_ecology.stake_10_is_pro_nlhe': null,
                    'player_ecology.stake_20_is_pro_nlhe': null,
                    'player_ecology.stake_50_is_pro_nlhe': null,
                    'player_ecology.stake_100_is_pro_nlhe': null,
                    'player_ecology.stake_200_is_pro_nlhe': null,
                    'player_ecology.stake_400_is_pro_nlhe': null,
                    'poker_mtt_summary.mtt_ticket_bonus': 'summary.poker_mtt_summary.mtt_ticket_bonus',
                    'poker_mtt_summary.mtt_ggr': 'summary.poker_mtt_summary.mtt_ggr',
                    'poker_mtt_summary.mtt_ngr': 'summary.poker_mtt_summary.mtt_ngr',
                    'poker_mtt_summary.mtt_total_buyin': 'summary.poker_mtt_summary.mtt_total_buyin',
                    'poker_mtt_summary.player_mtt_win_loss': 'summary.poker_mtt_summary.player_mtt_win_loss',
                    'poker_mtt_summary.poker_mtt_fees': 'summary.poker_mtt_summary.poker_mtt_fees',
                    'poker_nlhe_summary.is_pro': 'summary.poker_nlhe_summary.is_pro',
                    'poker_nlhe_summary.is_pro_shortdeck': 'summary.poker_nlhe_summary.is_pro_shortdeck',
                    'poker_nlhe_summary.nlhe_add_on_ggr_usd_pro': 'summary.poker_nlhe_summary.nlhe_add_on_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_ggr_usd_pro': 'summary.poker_nlhe_summary.nlhe_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_insurance_ggr_usd_pro': 'summary.poker_nlhe_summary.nlhe_insurance_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_jackpot_ggr_usd_pro': 'summary.poker_nlhe_summary.nlhe_jackpot_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_rakes_ggr_usd_pro': 'summary.poker_nlhe_summary.nlhe_rakes_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_micro_ggr_usd_pro': 'summary.poker_nlhe_summary.nlhe_micro_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_low_ggr_usd_pro': 'summary.poker_nlhe_summary.nlhe_low_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_medium_ggr_usd_pro': 'summary.poker_nlhe_summary.nlhe_medium_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_high_ggr_usd_pro': 'summary.poker_nlhe_summary.nlhe_high_ggr_usd_pro',
                    'poker_nlhe_summary.player_nlhe_shortdeck_win_loss_lifetime': null,
                    'poker_nlhe_summary.ggr_poker_prev_180_days': null,
                    'poker_nlhe_summary.player_nlhe_shortdeck_win_loss_prev_180_days': null,
                    'poker_nlhe_summary.shortdeck_add_on_ggr_usd_pro': 'summary.poker_nlhe_summary.shortdeck_add_on_ggr_usd_pro',
                    'poker_nlhe_summary.shortdeck_ggr_usd_pro': 'summary.poker_nlhe_summary.shortdeck_ggr_usd_pro',
                    'poker_nlhe_summary.shortdeck_insurance_ggr_usd_pro': 'summary.poker_nlhe_summary.shortdeck_insurance_ggr_usd_pro',
                    'poker_nlhe_summary.shortdeck_rakes_ggr_usd_pro': 'summary.poker_nlhe_summary.shortdeck_rakes_ggr_usd_pro',
                    'poker_plo_summary.plo_ggr': 'summary.poker_plo_summary.plo_ggr',
                    'poker_summary.poker_addons': 'summary.poker_summary.poker_addons',
                    'poker_summary.poker_addons_add_on': 'summary.poker_summary.poker_addons_add_on',
                    'poker_summary.poker_addons_guess': 'summary.poker_summary.poker_addons_guess',
                    'poker_summary.poker_addons_insurance': 'summary.poker_summary.poker_addons_insurance',
                    'poker_summary.poker_ggr': 'summary.poker_summary.poker_ggr',
                    'poker_summary.poker_ggr_usd_pro_ecology_modified': 'summary.poker_summary.poker_ggr_usd_pro_ecology_modified',
                    'poker_summary.poker_ngr': 'summary.poker_summary.poker_ngr',
                    'poker_summary.poker_rake': 'summary.poker_summary.poker_rake',
                    'poker_summary.poker_share': 'summary.poker_summary.poker_share',
                    'share_summary.casino_share': 'summary.share_summary.casino_share',
                    'share_summary.global_spins_expected_ggr': 'summary.share_summary.global_spins_expected_ggr',
                    'share_summary.mtt_ggr': 'summary.share_summary.mtt_ggr',
                    'share_summary.plo_ggr': 'summary.share_summary.plo_ggr',
                    'share_summary.poker_share': 'summary.share_summary.poker_share',
                    'share_summary.sports_settled_bet': 'summary.share_summary.sports_settled_bet',
                    'sports_summary.sports_settled_bet': 'summary.sports_summary.sports_settled_bet',
                    'sports_summary.sports_ggr': 'summary.sports_summary.sports_ggr',
                };

                return summaryMapper[m];
            })
        );
        createMap<PlayerLevelReportV2ViewModelKeys, PlayerLevelReportV2DownloadFields>(
            mapper,
            PlayerLevelReportV2ViewModelKeysTypeName,
            PlayerLevelReportV2DownloadFieldsTypeName,
            constructUsing(m => {
                const itemsMapper: Record<PlayerLevelReportV2ViewModelKeys, PlayerLevelReportV2DownloadFields> = {
                    iso_alpha2_country_code: 'iso_alpha2_country_code',
                    path: 'path',
                    referrer_agent_id: 'referrer_agent_id',
                    referrer_agent_username: 'referrer_agent_username',
                    uid: 'uid',
                    username: 'username',
                    'total_ggr_summary.ggr': 'total_ggr_summary.ggr',
                    'bonus_cost_summary.cash_realized_bonus': 'bonus_cost_summary.cash_realized_bonus',
                    'bonus_cost_summary.cost_ftd_bonus_pro': 'bonus_cost_summary.cost_ftd_bonus_pro',
                    'bonus_cost_summary.cost_psp_fee_pro': 'bonus_cost_summary.cost_psp_fee_pro',
                    'bonus_cost_summary.cost_tournament_overlay': 'bonus_cost_summary.cost_tournament_overlay',
                    'bonus_cost_summary.cost_tournament_overlay_excl_freeroll': 'bonus_cost_summary.cost_tournament_overlay_excl_freeroll',
                    'bonus_cost_summary.mtt_ticket_bonus': 'bonus_cost_summary.mtt_ticket_bonus',
                    'bonus_cost_summary.poker_bonus': 'bonus_cost_summary.poker_bonus',
                    'bonus_cost_summary.psp_fee': 'bonus_cost_summary.psp_fee',
                    'casino_summary.casino_bonus': 'casino_summary.casino_bonus',
                    'casino_summary.casino_ggr': 'casino_summary.casino_ggr',
                    'casino_summary.casino_ngr': 'casino_summary.casino_ngr',
                    'casino_summary.casino_share': 'casino_summary.casino_share',
                    'casino_summary.external_casino_ggr': 'casino_summary.external_casino_ggr',
                    'casino_summary.internal_casino_ggr': 'casino_summary.internal_casino_ggr',
                    'casino_summary.casino_wager': 'casino_summary.casino_wager',
                    'global_spins_summary.cost_global_spins_tickets': 'global_spins_summary.cost_global_spins_tickets',
                    'global_spins_summary.global_spins_expected_ggr': 'global_spins_summary.global_spins_expected_ggr',
                    'global_spins_summary.global_spins_ggr': 'global_spins_summary.global_spins_expected_ggr',
                    'player_activity_summary.cash_game_hands_played': 'player_activity_summary.cash_game_hands_played',
                    'player_activity_summary.ecology_flag': 'player_activity_summary.ecology_flag',
                    'player_activity_summary.mtt_played': 'player_activity_summary.mtt_played',
                    'player_activity_summary.player_profit_loss': 'player_activity_summary.player_profit_loss',
                    'player_activity_summary.player_win_loss': 'player_activity_summary.player_win_loss',
                    'player_activity_summary.net_deposit': 'player_activity_summary.net_deposit',
                    'player_ecology.ecology_flag': 'player_ecology.ecology_flag',
                    'player_ecology.is_pro_shortdeck': 'player_ecology.is_pro_shortdeck',
                    'player_ecology.is_pro_zoom': 'player_ecology.is_pro_zoom',
                    'player_ecology.stake_2_is_pro_nlhe': 'player_ecology.stake_2_is_pro_nlhe',
                    'player_ecology.stake_4_is_pro_nlhe': 'player_ecology.stake_4_is_pro_nlhe',
                    'player_ecology.stake_10_is_pro_nlhe': 'player_ecology.stake_10_is_pro_nlhe',
                    'player_ecology.stake_20_is_pro_nlhe': 'player_ecology.stake_20_is_pro_nlhe',
                    'player_ecology.stake_50_is_pro_nlhe': 'player_ecology.stake_50_is_pro_nlhe',
                    'player_ecology.stake_100_is_pro_nlhe': 'player_ecology.stake_100_is_pro_nlhe',
                    'player_ecology.stake_200_is_pro_nlhe': 'player_ecology.stake_200_is_pro_nlhe',
                    'player_ecology.stake_400_is_pro_nlhe': 'player_ecology.stake_400_is_pro_nlhe',
                    'poker_mtt_summary.mtt_ticket_bonus': 'poker_mtt_summary.mtt_ticket_bonus',
                    'poker_mtt_summary.mtt_ggr': 'poker_mtt_summary.mtt_ggr',
                    'poker_mtt_summary.mtt_ngr': 'poker_mtt_summary.mtt_ngr',
                    'poker_mtt_summary.mtt_total_buyin': 'poker_mtt_summary.mtt_total_buyin',
                    'poker_mtt_summary.player_mtt_win_loss': 'poker_mtt_summary.player_mtt_win_loss',
                    'poker_mtt_summary.poker_mtt_fees': 'poker_mtt_summary.poker_mtt_fees',
                    'poker_nlhe_summary.is_pro': 'poker_nlhe_summary.is_pro',
                    'poker_nlhe_summary.is_pro_shortdeck': 'poker_nlhe_summary.is_pro_shortdeck',
                    'poker_nlhe_summary.nlhe_add_on_ggr_usd_pro': 'poker_nlhe_summary.nlhe_add_on_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_ggr_usd_pro': 'poker_nlhe_summary.nlhe_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_insurance_ggr_usd_pro': 'poker_nlhe_summary.nlhe_insurance_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_jackpot_ggr_usd_pro': 'poker_nlhe_summary.nlhe_jackpot_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_rakes_ggr_usd_pro': 'poker_nlhe_summary.nlhe_rakes_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_micro_ggr_usd_pro': 'poker_nlhe_summary.nlhe_micro_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_low_ggr_usd_pro': 'poker_nlhe_summary.nlhe_low_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_medium_ggr_usd_pro': 'poker_nlhe_summary.nlhe_medium_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_high_ggr_usd_pro': 'poker_nlhe_summary.nlhe_high_ggr_usd_pro',
                    'poker_nlhe_summary.player_nlhe_shortdeck_win_loss_lifetime':
                        'poker_nlhe_summary.player_nlhe_shortdeck_win_loss_lifetime',
                    'poker_nlhe_summary.ggr_poker_prev_180_days': 'poker_nlhe_summary.ggr_poker_prev_180_days',
                    'poker_nlhe_summary.player_nlhe_shortdeck_win_loss_prev_180_days':
                        'poker_nlhe_summary.player_nlhe_shortdeck_win_loss_prev_180_days',
                    'poker_nlhe_summary.shortdeck_add_on_ggr_usd_pro': 'poker_nlhe_summary.shortdeck_add_on_ggr_usd_pro',
                    'poker_nlhe_summary.shortdeck_ggr_usd_pro': 'poker_nlhe_summary.shortdeck_ggr_usd_pro',
                    'poker_nlhe_summary.shortdeck_insurance_ggr_usd_pro': 'poker_nlhe_summary.shortdeck_insurance_ggr_usd_pro',
                    'poker_nlhe_summary.shortdeck_rakes_ggr_usd_pro': 'poker_nlhe_summary.shortdeck_rakes_ggr_usd_pro',
                    'poker_plo_summary.plo_ggr': 'poker_plo_summary.plo_ggr',
                    'poker_summary.poker_addons': 'poker_summary.poker_addons',
                    'poker_summary.poker_addons_add_on': 'poker_summary.poker_addons_add_on',
                    'poker_summary.poker_addons_guess': 'poker_summary.poker_addons_guess',
                    'poker_summary.poker_addons_insurance': 'poker_summary.poker_addons_insurance',
                    'poker_summary.poker_ggr': 'poker_summary.poker_ggr',
                    'poker_summary.poker_ggr_usd_pro_ecology_modified': 'poker_summary.poker_ggr_usd_pro_ecology_modified',
                    'poker_summary.poker_ngr': 'poker_summary.poker_ngr',
                    'poker_summary.poker_rake': 'poker_summary.poker_rake',
                    'poker_summary.poker_share': 'poker_summary.poker_share',
                    'share_summary.casino_share': 'share_summary.casino_share',
                    'share_summary.global_spins_expected_ggr': 'share_summary.global_spins_expected_ggr',
                    'share_summary.mtt_ggr': 'share_summary.mtt_ggr',
                    'share_summary.plo_ggr': 'share_summary.plo_ggr',
                    'share_summary.poker_share': 'share_summary.poker_share',
                    'share_summary.sports_settled_bet': 'share_summary.sports_settled_bet',
                    'sports_summary.sports_settled_bet': 'sports_summary.sports_settled_bet',
                    'sports_summary.sports_ggr': 'sports_summary.sports_ggr',
                };

                return itemsMapper[m];
            })
        );
        createMap<PlayerLevelReportV2ViewModelKeys, PlayerLevelReportV2ColumnSettingKeys>(
            mapper,
            PlayerLevelReportV2ViewModelKeysTypeName,
            PlayerLevelReportV2ColumnSettingKeysTypeName,
            constructUsing(m => {
                type FieldsToMap = Exclude<PlayerLevelReportV2ViewModelKeys, PlayerLevelReportV2ColumnSettingKeys>;
                const isNever: IsNever<FieldsToMap> = true;
                if (!isNever) {
                    throw new Error('Mapping should be defined for all values');
                }
                return m;
            })
        );
        createMap<PlayerLevelReportV2ColumnSettingKeys, PlayerLevelReportV2ViewModelKeys>(
            mapper,
            PlayerLevelReportV2ColumnSettingKeysTypeName,
            PlayerLevelReportV2ViewModelKeysTypeName,
            constructUsing(m => {
                type FieldsToMap = Exclude<PlayerLevelReportV2ColumnSettingKeys, PlayerLevelReportV2ViewModelKeys | '__type'>;
                const isNever: IsNever<FieldsToMap> = true;
                if (!isNever) {
                    throw new Error('Mapping should be defined for all values');
                }
                return m === '__type' ? undefined : m;
            })
        );
        createMap<PlayerLevelReportV2ViewModelKeys, PlayerLevelReportV2SortKeys>(
            mapper,
            PlayerLevelReportV2ViewModelKeysTypeName,
            PlayerLevelReportV2SortKeysTypeName,
            constructUsing(m => {
                const fieldsMapper: Record<PlayerLevelReportV2ViewModelKeys, PlayerLevelReportV2SortKeys> = {
                    iso_alpha2_country_code: 'iso_alpha2_country_code',
                    path: 'path',
                    referrer_agent_id: 'referrer_agent_id',
                    referrer_agent_username: 'referrer_agent_username',
                    uid: 'uid',
                    username: 'username',
                    'total_ggr_summary.ggr': 'ggr',
                    'bonus_cost_summary.cash_realized_bonus': 'cash_realized_bonus',
                    'bonus_cost_summary.cost_ftd_bonus_pro': 'cost_ftd_bonus_pro',
                    'bonus_cost_summary.cost_psp_fee_pro': 'cost_psp_fee_pro',
                    'bonus_cost_summary.cost_tournament_overlay': 'cost_tournament_overlay',
                    'bonus_cost_summary.cost_tournament_overlay_excl_freeroll': 'cost_tournament_overlay_excl_freeroll',
                    'bonus_cost_summary.mtt_ticket_bonus': 'mtt_ticket_bonus',
                    'bonus_cost_summary.poker_bonus': 'poker_bonus',
                    'bonus_cost_summary.psp_fee': 'psp_fee',
                    'casino_summary.casino_bonus': 'casino_bonus',
                    'casino_summary.casino_ggr': 'casino_ggr',
                    'casino_summary.casino_ngr': 'casino_ngr',
                    'casino_summary.casino_share': 'casino_share',
                    'casino_summary.external_casino_ggr': 'external_casino_ggr',
                    'casino_summary.internal_casino_ggr': 'internal_casino_ggr',
                    'casino_summary.casino_wager': 'casino_wager',
                    'global_spins_summary.cost_global_spins_tickets': 'cost_global_spins_tickets',
                    'global_spins_summary.global_spins_expected_ggr': 'global_spins_expected_ggr',
                    'global_spins_summary.global_spins_ggr': 'global_spins_ggr',
                    'player_activity_summary.cash_game_hands_played': 'cash_game_hands_played',
                    'player_activity_summary.ecology_flag': 'ecology_flag',
                    'player_activity_summary.mtt_played': 'mtt_played',
                    'player_activity_summary.player_profit_loss': 'player_profit_loss',
                    'player_activity_summary.player_win_loss': 'player_win_loss',
                    'player_activity_summary.net_deposit': 'net_deposit',
                    'player_ecology.ecology_flag': 'ecology_flag',
                    'player_ecology.is_pro_shortdeck': 'is_pro_shortdeck',
                    'player_ecology.is_pro_zoom': 'is_pro_zoom',
                    'player_ecology.stake_2_is_pro_nlhe': 'stake_2_is_pro_nlhe',
                    'player_ecology.stake_4_is_pro_nlhe': 'stake_4_is_pro_nlhe',
                    'player_ecology.stake_10_is_pro_nlhe': 'stake_10_is_pro_nlhe',
                    'player_ecology.stake_20_is_pro_nlhe': 'stake_20_is_pro_nlhe',
                    'player_ecology.stake_50_is_pro_nlhe': 'stake_50_is_pro_nlhe',
                    'player_ecology.stake_100_is_pro_nlhe': 'stake_100_is_pro_nlhe',
                    'player_ecology.stake_200_is_pro_nlhe': 'stake_200_is_pro_nlhe',
                    'player_ecology.stake_400_is_pro_nlhe': 'stake_400_is_pro_nlhe',
                    'poker_mtt_summary.mtt_ticket_bonus': 'mtt_ticket_bonus',
                    'poker_mtt_summary.mtt_ggr': 'mtt_ggr',
                    'poker_mtt_summary.mtt_ngr': 'mtt_ngr',
                    'poker_mtt_summary.mtt_total_buyin': 'mtt_total_buyin',
                    'poker_mtt_summary.player_mtt_win_loss': 'player_mtt_win_loss',
                    'poker_mtt_summary.poker_mtt_fees': 'poker_mtt_fees',
                    'poker_nlhe_summary.is_pro': 'is_pro',
                    'poker_nlhe_summary.is_pro_shortdeck': 'is_pro_shortdeck',
                    'poker_nlhe_summary.nlhe_add_on_ggr_usd_pro': 'nlhe_add_on_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_ggr_usd_pro': 'nlhe_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_insurance_ggr_usd_pro': 'nlhe_insurance_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_jackpot_ggr_usd_pro': 'nlhe_jackpot_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_rakes_ggr_usd_pro': 'nlhe_rakes_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_micro_ggr_usd_pro': 'nlhe_micro_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_low_ggr_usd_pro': 'nlhe_low_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_medium_ggr_usd_pro': 'nlhe_medium_ggr_usd_pro',
                    'poker_nlhe_summary.nlhe_high_ggr_usd_pro': 'nlhe_high_ggr_usd_pro',
                    'poker_nlhe_summary.player_nlhe_shortdeck_win_loss_lifetime': 'player_nlhe_shortdeck_win_loss_lifetime',
                    'poker_nlhe_summary.ggr_poker_prev_180_days': 'ggr_poker_prev_180_days',
                    'poker_nlhe_summary.player_nlhe_shortdeck_win_loss_prev_180_days': 'player_nlhe_shortdeck_win_loss_prev_180_days',
                    'poker_nlhe_summary.shortdeck_add_on_ggr_usd_pro': 'shortdeck_add_on_ggr_usd_pro',
                    'poker_nlhe_summary.shortdeck_ggr_usd_pro': 'shortdeck_ggr_usd_pro',
                    'poker_nlhe_summary.shortdeck_insurance_ggr_usd_pro': 'shortdeck_insurance_ggr_usd_pro',
                    'poker_nlhe_summary.shortdeck_rakes_ggr_usd_pro': 'shortdeck_rakes_ggr_usd_pro',
                    'poker_plo_summary.plo_ggr': 'plo_ggr',
                    'poker_summary.poker_addons': 'poker_addons',
                    'poker_summary.poker_addons_add_on': 'poker_addons_add_on',
                    'poker_summary.poker_addons_guess': 'poker_addons_guess',
                    'poker_summary.poker_addons_insurance': 'poker_addons_insurance',
                    'poker_summary.poker_ggr': 'poker_ggr',
                    'poker_summary.poker_ggr_usd_pro_ecology_modified': 'poker_ggr_usd_pro_ecology_modified',
                    'poker_summary.poker_ngr': 'poker_ngr',
                    'poker_summary.poker_rake': 'poker_rake',
                    'poker_summary.poker_share': 'poker_share',
                    'share_summary.casino_share': 'casino_share',
                    'share_summary.global_spins_expected_ggr': 'global_spins_expected_ggr',
                    'share_summary.mtt_ggr': 'mtt_ggr',
                    'share_summary.plo_ggr': 'plo_ggr',
                    'share_summary.poker_share': 'poker_share',
                    'share_summary.sports_settled_bet': 'sports_settled_bet',
                    'sports_summary.sports_settled_bet': 'sports_settled_bet',
                    'sports_summary.sports_ggr': 'sports_ggr',
                };
                return fieldsMapper[m];
            })
        );
        createMap<PlayerLevelReportV2SortKeys, PlayerLevelReportV2ViewModelKeys>(
            mapper,
            PlayerLevelReportV2SortKeysTypeName,
            PlayerLevelReportV2ViewModelKeysTypeName,
            constructUsing(m => {
                const fieldsMapper: Record<PlayerLevelReportV2SortKeys, PlayerLevelReportV2ViewModelKeys> = {
                    iso_alpha2_country_code: 'iso_alpha2_country_code',
                    path: 'path',
                    referrer_agent_id: 'referrer_agent_id',
                    referrer_agent_username: 'referrer_agent_username',
                    uid: 'uid',
                    username: 'username',
                    ggr: 'total_ggr_summary.ggr',
                    cash_realized_bonus: 'bonus_cost_summary.cash_realized_bonus',
                    cost_ftd_bonus_pro: 'bonus_cost_summary.cost_ftd_bonus_pro',
                    cost_psp_fee_pro: 'bonus_cost_summary.cost_psp_fee_pro',
                    cost_tournament_overlay: 'bonus_cost_summary.cost_tournament_overlay',
                    cost_tournament_overlay_excl_freeroll: 'bonus_cost_summary.cost_tournament_overlay_excl_freeroll',
                    mtt_ticket_bonus: 'bonus_cost_summary.mtt_ticket_bonus',
                    poker_bonus: 'bonus_cost_summary.poker_bonus',
                    psp_fee: 'bonus_cost_summary.psp_fee',
                    casino_bonus: 'casino_summary.casino_bonus',
                    casino_ggr: 'casino_summary.casino_ggr',
                    casino_ngr: 'casino_summary.casino_ngr',
                    casino_share: 'casino_summary.casino_share',
                    casino_wager: 'casino_summary.casino_wager',
                    external_casino_ggr: 'casino_summary.external_casino_ggr',
                    internal_casino_ggr: 'casino_summary.internal_casino_ggr',
                    cost_global_spins_tickets: 'global_spins_summary.cost_global_spins_tickets',
                    global_spins_expected_ggr: 'global_spins_summary.global_spins_expected_ggr',
                    global_spins_ggr: 'global_spins_summary.global_spins_ggr',
                    cash_game_hands_played: 'player_activity_summary.cash_game_hands_played',
                    mtt_played: 'player_activity_summary.mtt_played',
                    player_profit_loss: 'player_activity_summary.player_profit_loss',
                    player_win_loss: 'player_activity_summary.player_win_loss',
                    net_deposit: 'player_activity_summary.net_deposit',
                    mtt_ggr: 'poker_mtt_summary.mtt_ggr',
                    mtt_ngr: 'poker_mtt_summary.mtt_ngr',
                    mtt_total_buyin: 'poker_mtt_summary.mtt_total_buyin',
                    player_mtt_win_loss: 'poker_mtt_summary.player_mtt_win_loss',
                    poker_mtt_fees: 'poker_mtt_summary.poker_mtt_fees',
                    is_pro: 'poker_nlhe_summary.is_pro',
                    nlhe_add_on_ggr_usd_pro: 'poker_nlhe_summary.nlhe_add_on_ggr_usd_pro',
                    nlhe_ggr_usd_pro: 'poker_nlhe_summary.nlhe_ggr_usd_pro',
                    nlhe_insurance_ggr_usd_pro: 'poker_nlhe_summary.nlhe_insurance_ggr_usd_pro',
                    nlhe_jackpot_ggr_usd_pro: 'poker_nlhe_summary.nlhe_jackpot_ggr_usd_pro',
                    nlhe_rakes_ggr_usd_pro: 'poker_nlhe_summary.nlhe_rakes_ggr_usd_pro',
                    nlhe_micro_ggr_usd_pro: 'poker_nlhe_summary.nlhe_micro_ggr_usd_pro',
                    nlhe_low_ggr_usd_pro: 'poker_nlhe_summary.nlhe_low_ggr_usd_pro',
                    nlhe_medium_ggr_usd_pro: 'poker_nlhe_summary.nlhe_medium_ggr_usd_pro',
                    nlhe_high_ggr_usd_pro: 'poker_nlhe_summary.nlhe_high_ggr_usd_pro',
                    player_nlhe_shortdeck_win_loss_lifetime: 'poker_nlhe_summary.player_nlhe_shortdeck_win_loss_lifetime',
                    ggr_poker_prev_180_days: 'poker_nlhe_summary.ggr_poker_prev_180_days',
                    player_nlhe_shortdeck_win_loss_prev_180_days: 'poker_nlhe_summary.player_nlhe_shortdeck_win_loss_prev_180_days',
                    shortdeck_add_on_ggr_usd_pro: 'poker_nlhe_summary.shortdeck_add_on_ggr_usd_pro',
                    shortdeck_ggr_usd_pro: 'poker_nlhe_summary.shortdeck_ggr_usd_pro',
                    shortdeck_insurance_ggr_usd_pro: 'poker_nlhe_summary.shortdeck_insurance_ggr_usd_pro',
                    shortdeck_rakes_ggr_usd_pro: 'poker_nlhe_summary.shortdeck_rakes_ggr_usd_pro',
                    plo_ggr: 'poker_plo_summary.plo_ggr',
                    poker_addons: 'poker_summary.poker_addons',
                    poker_addons_add_on: 'poker_summary.poker_addons_add_on',
                    poker_addons_guess: 'poker_summary.poker_addons_guess',
                    poker_addons_insurance: 'poker_summary.poker_addons_insurance',
                    poker_ggr: 'poker_summary.poker_ggr',
                    poker_ggr_usd_pro_ecology_modified: 'poker_summary.poker_ggr_usd_pro_ecology_modified',
                    poker_ngr: 'poker_summary.poker_ngr',
                    poker_rake: 'poker_summary.poker_rake',
                    poker_share: 'poker_summary.poker_share',
                    sports_settled_bet: 'sports_summary.sports_settled_bet',
                    sports_ggr: 'sports_summary.sports_ggr',
                    ecology_flag: 'player_ecology.ecology_flag',
                    is_pro_shortdeck: 'player_ecology.is_pro_shortdeck',
                    is_pro_zoom: 'player_ecology.is_pro_zoom',
                    stake_2_is_pro_nlhe: 'player_ecology.stake_2_is_pro_nlhe',
                    stake_4_is_pro_nlhe: 'player_ecology.stake_4_is_pro_nlhe',
                    stake_10_is_pro_nlhe: 'player_ecology.stake_10_is_pro_nlhe',
                    stake_20_is_pro_nlhe: 'player_ecology.stake_20_is_pro_nlhe',
                    stake_50_is_pro_nlhe: 'player_ecology.stake_50_is_pro_nlhe',
                    stake_100_is_pro_nlhe: 'player_ecology.stake_100_is_pro_nlhe',
                    stake_200_is_pro_nlhe: 'player_ecology.stake_200_is_pro_nlhe',
                    stake_400_is_pro_nlhe: 'player_ecology.stake_400_is_pro_nlhe',
                };
                return fieldsMapper[m];
            })
        );
    }
}
